/**
 * CategoryRow Component
 * Expandable category grouping header for budget table
 */

import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import VarianceIndicator from './VarianceIndicator';

export interface CategoryRowProps {
  groupLabel: string;
  summary: {
    originalBudget: number;
    approvedChanges: number;
    revisedBudget: number;
    committed: number;
    actual: number;
    variance: number;
  };
  isExpanded: boolean;
  onToggle: () => void;
  itemCount: number;
}

const CategoryRow: React.FC<CategoryRowProps> = ({
  groupLabel,
  summary,
  isExpanded,
  onToggle,
  itemCount,
}) => {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const variancePercent =
    summary.revisedBudget > 0 ? (summary.variance / summary.revisedBudget) * 100 : 0;

  return (
    <tr className="bg-gray-50 border-y border-gray-300 font-medium hover:bg-gray-100 transition-colors">
      {/* Group Label with Expand/Collapse */}
      <td colSpan={2} className="px-4 py-3">
        <button
          onClick={onToggle}
          className="flex items-center space-x-2 text-sm text-gray-900 hover:text-brand-600 transition-colors"
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="font-semibold">{groupLabel}</span>
          <span className="text-xs text-gray-500 font-normal">({itemCount} items)</span>
        </button>
      </td>

      {/* Original Budget */}
      <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
        {formatCurrency(summary.originalBudget)}
      </td>

      {/* Approved Changes */}
      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
        {summary.approvedChanges !== 0 && (
          <span className={summary.approvedChanges > 0 ? 'text-green-600' : 'text-red-600'}>
            {summary.approvedChanges > 0 ? '+' : ''}
            {formatCurrency(summary.approvedChanges)}
          </span>
        )}
        {summary.approvedChanges === 0 && <span className="text-gray-400">-</span>}
      </td>

      {/* Revised Budget */}
      <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
        {formatCurrency(summary.revisedBudget)}
      </td>

      {/* Committed */}
      <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
        {formatCurrency(summary.committed)}
      </td>

      {/* Actual */}
      <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
        {formatCurrency(summary.actual)}
      </td>

      {/* Forecast - Empty for group row */}
      <td className="px-4 py-3"></td>

      {/* Variance */}
      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
        <VarianceIndicator
          variance={summary.variance}
          variancePercent={variancePercent}
          size="sm"
        />
      </td>

      {/* % Used - Empty for group row */}
      <td className="px-4 py-3"></td>

      {/* Actions - Empty for group row */}
      <td className="px-4 py-3"></td>
    </tr>
  );
};

export default CategoryRow;
